import { api } from "../api";

export interface FormFieldConfig {
  id: string;
  name: string;
  label: string;
  type:
    | "text"
    | "email"
    | "number"
    | "select"
    | "checkbox"
    | "radio"
    | "textarea"
    | "date"
    | "file";
  required: boolean;
  placeholder?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
    customMessage?: string;
  };
  options?: { label: string; value: string }[];
  conditional?: {
    field: string;
    operator: "equals" | "notEquals" | "contains";
    value: any;
  };
  order: number;
}

export interface RegistrationConfig {
  allowSelfRegistration: boolean;
  verificationMode: "auto" | "manual";
  requirePayment: boolean;
  defaultFeeAmount?: number;
  requireProfilePicture: boolean;
  requireDocuments: string[];
  paymentGateway?: string;
  showBranchPreference?: boolean;
  formFields: FormFieldConfig[];
}

export interface PatronTypeRegistrationConfig {
  requireDocuments?: string[];
  registrationFee?: number;
  autoVerification?: boolean;
  customFields?: FormFieldConfig[];
}

export interface RegisterPatronData {
  patronTypeId: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  formData?: Record<string, any>;
  profilePictureKey?: string;
  documentKeys?: string[];
  documentTypes?: Record<string, string>; // documentTypeName -> fileName (temporary, backend will map to keys)
  paymentTransactionId?: string;
  homeBranchId?: string;
}

export const registrationApi = {
  /**
   * Get registration configuration (public)
   */
  async getRegistrationConfig(
    patronTypeId?: string,
    tenantSlug?: string
  ): Promise<RegistrationConfig> {
    const params: Record<string, string> = {
      domain: window.location.hostname,
    };
    if (patronTypeId) params.patronTypeId = patronTypeId;
    // In development, only pass tenantSlug if explicitly provided
    // Backend will auto-fallback to first tenant if not provided
    if (tenantSlug && import.meta.env.DEV) {
      params.tenantSlug = tenantSlug;
    }
    const response = await api.get("/registration-config", { params });
    return response.data;
  },

  /**
   * Get registration configuration (admin)
   */
  async getAdminRegistrationConfig(patronTypeId?: string): Promise<RegistrationConfig> {
    const params = patronTypeId ? { patronTypeId } : {};
    const response = await api.get("/registration-config/admin", { params });
    return response.data;
  },

  /**
   * Update registration configuration (admin)
   */
  async updateRegistrationConfig(config: Partial<RegistrationConfig>): Promise<RegistrationConfig> {
    const response = await api.put("/registration-config/admin", config);
    return response.data;
  },

  /**
   * Update patron-type-specific registration configuration (admin)
   */
  async updatePatronTypeRegistrationConfig(
    patronTypeId: string,
    config: PatronTypeRegistrationConfig
  ): Promise<void> {
    await api.put(`/registration-config/admin/patron-type/${patronTypeId}`, config);
  },

  /**
   * Submit patron registration (public)
   */
  async submitRegistration(
    data: RegisterPatronData,
    files?: File[],
    tenantSlug?: string
  ): Promise<{
    success: boolean;
    message: string;
    userId?: string;
    status: "pending" | "approved" | "rejected";
  }> {
    const formData = new FormData();

    // Add text fields
    Object.entries(data).forEach(([key, value]) => {
      if (key !== "files" && value !== undefined && value !== null) {
        if (typeof value === "object" && !(value instanceof File)) {
          // Stringify objects (formData, documentTypes)
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value as string);
        }
      }
    });

    // Add files - backend expects all files with field name "files"
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file);
      });
    }

    // Add tenantSlug as query parameter if provided
    const params: Record<string, string> = {
      domain: window.location.hostname,
    };
    if (tenantSlug && import.meta.env.DEV) {
      params.tenantSlug = tenantSlug;
    }

    const response = await api.post("/registration", formData, {
      params,
      timeout: 120_000,
    });
    return response.data;
  },

  /**
   * Get registration status by token
   */
  async getRegistrationStatus(token: string): Promise<{
    status: "pending" | "approved" | "rejected";
    message: string;
  }> {
    const response = await api.get(`/registration/status/${token}`);
    return response.data;
  },

  /**
   * Check payment status (authenticated)
   */
  async checkPaymentStatus(): Promise<{
    paymentRequired: boolean;
    paymentCompleted: boolean;
    registrationFee: number;
    schoolShare?: number;
    platformShare?: number;
    canAccessServices: boolean;
  }> {
    const response = await api.get("/registration/payment-status");
    return response.data;
  },

  /**
   * Complete payment (authenticated)
   */
  async completePayment(paymentTransactionId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await api.post("/registration/complete-payment", {
      paymentTransactionId,
    });
    return response.data;
  },

  /**
   * Resend verification email (authenticated)
   */
  async resendVerificationEmail(): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await api.post("/registration/resend-verification");
    return response.data;
  },

  /**
   * Resend verification email by email (public - when user cannot log in)
   */
  async resendVerificationEmailByEmail(
    email: string,
    tenantSlug?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post("/registration/resend-verification-email", {
      email,
      tenantSlug,
    });
    return response.data;
  },

  /**
   * Get active branches for the registration form branch-preference dropdown (public)
   */
  async getPublicBranches(
    tenantSlug?: string
  ): Promise<{ id: string; name: string; code: string; isMain: boolean }[]> {
    const params: Record<string, string> = {
      domain: window.location.hostname,
    };
    if (tenantSlug) params.tenantSlug = tenantSlug;
    const response = await api.get("/registration/public/branches", { params });
    return response.data;
  },
};
